import type {
  OrderSummary,
  OrdersOverview,
  ReleaseRow,
  ReleasesResponse,
} from '@/api/orders';
import { isOpenProblem, isResolvedProblem } from '@/api/packages';
import { stageOf } from '@/features/orders/lib';
import { releaseBand, releaseWaitingOn } from '@/features/orders/releasesLib';

// ---------------------------------------------------------------------------
// The overview calendar's facts. Every dated thing the app already knows about
// a journey — the window closing, the street date, the vendor's ship date, a
// parcel's ETA or delivery, a receiving booked — becomes one item on one day.
// Nothing here is fetched: the journey list, the overview and the releases
// read are the whole input, so the calendar can never disagree with the
// queues built from the same payloads.
// ---------------------------------------------------------------------------

export type CalendarKind = 'due' | 'street' | 'ships' | 'arriving' | 'delivered' | 'received';

/** Display order inside a day, and the legend's order. */
export const CALENDAR_KINDS: readonly CalendarKind[] = [
  'due', 'street', 'ships', 'arriving', 'delivered', 'received',
];

export const KIND_LABELS: Record<CalendarKind, string> = {
  due: 'Order by',
  street: 'Street date',
  ships: 'Ships',
  arriving: 'Arriving',
  delivered: 'Delivered',
  received: 'Received',
};

/** The same kinds once their day has passed without the thing happening. */
export const PAST_DUE_LABELS: Partial<Record<CalendarKind, string>> = {
  due: 'Order by',
  street: 'Street date',
  arriving: 'Expected',
};

export interface CalendarItem {
  key: string;
  kind: CalendarKind;
  /** Local calendar day, YYYY-MM-DD. */
  day: string;
  vendor: string;
  label: string | null;
  logoUrl: string | null;
  /** Where the chip goes: the journey, or the packages feed for a parcel with none. */
  to: string;
  /** Parcels or receivings folded into this one chip. */
  count: number;
  /**
   * The kind's own fact, never the count: carriers ("UPS, FedEx") or
   * "Delivery problem" for parcels, the release state ("12 hidden") for a
   * street date, receiving numbers ("#21405") for a receiving.
   */
  fact: string | null;
  /** The day has passed and what it promised has not happened. */
  overdue: boolean;
}

// ---- Day arithmetic on YYYY-MM-DD strings ---------------------------------
// Everything is done on date strings so a day never shifts with the clock:
// a deadline of 2026-09-09 is the 9th whatever the hour.

export function toDayString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function todayDay(): string {
  return toDayString(new Date());
}

/**
 * The local calendar day of a value. Date-only strings are calendar dates
 * already; timestamps take the browser's day, the same rule the rest of the
 * app formats dates with and the server's calendar sync uses.
 */
export function localDay(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : toDayString(d);
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toDayString(d);
}

/** a − b in whole days. */
export function dayDiff(a: string, b: string): number {
  const utc = (day: string) => {
    const [y, m, d] = day.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((utc(a) - utc(b)) / 86_400_000);
}

/** The first day of the week holding `day`; weeks start on Sunday. */
export function weekStartOf(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  return addDays(day, -d.getDay());
}

/** "today" / "tomorrow" / "in 3 days" / "3 days ago". */
export function relativeDay(day: string, today: string): string {
  const n = dayDiff(day, today);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

// ---- Building the items ---------------------------------------------------

const TERMINAL = new Set(['cancelled', 'wont_order']);
const KIND_RANK = Object.fromEntries(CALENDAR_KINDS.map((k, i) => [k, i])) as Record<CalendarKind, number>;

/** EasyPost carrier codes as people say them; "FedExDefault" is an account-name artifact. */
function carrierName(carrier: string | null): string | null {
  if (!carrier) return null;
  const name = carrier.replace(/Default$/, '');
  return name === 'DHLExpress' ? 'DHL Express' : name;
}

function releaseFact(r: ReleaseRow): string | null {
  if (r.state === 'live') return 'live';
  if (r.state === 'hidden') return r.visibility ? `${r.visibility.toMakeLive} hidden` : 'hidden';
  return releaseWaitingOn(r)?.label ?? null;
}

interface Owner {
  id: string;
  vendor: string;
  label: string | null;
  logoUrl: string | null;
  to: string;
}

interface ParcelGroup extends CalendarItem {
  carriers: Set<string>;
  problem: boolean;
}

export function buildCalendarItems(input: {
  orders: OrderSummary[];
  overview: OrdersOverview | null;
  releases: ReleasesResponse | null;
  today: string;
}): CalendarItem[] {
  const { orders, overview, releases, today } = input;
  const items: CalendarItem[] = [];
  const parcels = new Map<string, ParcelGroup>();
  const receivings = new Map<string, CalendarItem & { ids: number[] }>();
  const releaseById = new Map<string, ReleaseRow>();
  for (const r of releases?.releases ?? []) releaseById.set(r.id, r);
  const orderById = new Map<string, OrderSummary>();
  for (const o of orders) orderById.set(o.id, o);

  const addParcel = (
    kind: 'arriving' | 'delivered',
    day: string,
    owner: Owner,
    pkg: { carrier: string | null; status: string; problemResolution?: string | null },
  ) => {
    const key = `${kind}:${owner.id}:${day}`;
    let group = parcels.get(key);
    if (!group) {
      group = {
        key, kind, day, vendor: owner.vendor, label: owner.label, logoUrl: owner.logoUrl, to: owner.to,
        count: 0, fact: null, overdue: false, carriers: new Set(), problem: false,
      };
      parcels.set(key, group);
      items.push(group);
    }
    group.count += 1;
    const carrier = carrierName(pkg.carrier);
    if (carrier) group.carriers.add(carrier);
    if (isOpenProblem(pkg)) group.problem = true;
    // An ETA that has passed with no delivery is late, and a failed delivery
    // is late whatever its ETA says.
    if (kind === 'arriving' && (group.problem || day < today)) group.overdue = true;
  };

  const addPackages = (owner: Owner, packages: Array<{ carrier: string | null; status: string; estDeliveryDate: string | null; deliveredAt: string | null; problemResolution?: string | null }>) => {
    for (const p of packages) {
      // A settled problem (lost and made good) never arrives, so it has no day.
      if (p.status === 'cancelled' || isResolvedProblem(p)) continue;
      if (p.status === 'delivered') {
        const day = localDay(p.deliveredAt);
        if (day) addParcel('delivered', day, owner, p);
        continue;
      }
      const day = localDay(p.estDeliveryDate);
      if (day) addParcel('arriving', day, owner, p);
    }
  };

  for (const o of orders) {
    if (o.archivedAt || TERMINAL.has(o.status)) continue;
    const owner: Owner = {
      id: o.id, vendor: o.vendor, label: o.label, logoUrl: o.logoUrl,
      to: `/journeys/${encodeURIComponent(o.id)}`,
    };
    const base = { vendor: o.vendor, label: o.label, logoUrl: o.logoUrl, to: owner.to, count: 1 };
    const stage = stageOf(o);

    if (o.status === 'announced' && o.orderDueDate) {
      const day = o.orderDueDate.slice(0, 10);
      items.push({ ...base, key: `due:${o.id}`, kind: 'due', day, fact: null, overdue: day < today });
    }
    if (o.embargoDate) {
      const day = o.embargoDate.slice(0, 10);
      const release = releaseById.get(o.id);
      items.push({
        ...base, key: `street:${o.id}`, kind: 'street', day,
        fact: release ? releaseFact(release) : null,
        // Only the live catalog read can say a passed street date is still
        // dark; without it a past street date is history, not a problem.
        overdue: release ? releaseBand(release) === 'overdue' : false,
      });
    }
    // The vendor's ship date matters until the goods land; on an arrived or
    // finished journey it is history and would only crowd the past weeks.
    if (o.expectedAvailability && (stage === 'to_order' || stage === 'on_order' || stage === 'in_transit')) {
      items.push({ ...base, key: `ships:${o.id}`, kind: 'ships', day: o.expectedAvailability.slice(0, 10), fact: null, overdue: false });
    }
    addPackages(owner, o.packages);
  }

  for (const p of overview?.unlinkedPackages ?? []) {
    const vendor = p.vendor || 'Unknown sender';
    addPackages({ id: `unlinked:${vendor}`, vendor, label: p.orderRef, logoUrl: null, to: '/packages' }, [p]);
  }

  for (const r of overview?.receivings ?? []) {
    const day = localDay(r.dateCreated);
    if (!day) continue;
    const key = `received:${r.orderId}:${day}`;
    let group = receivings.get(key);
    if (!group) {
      group = {
        key, kind: 'received', day, vendor: r.vendor, label: r.label,
        logoUrl: orderById.get(r.orderId)?.logoUrl ?? null,
        to: `/journeys/${encodeURIComponent(r.orderId)}?tab=receiving`,
        count: 0, fact: null, overdue: false, ids: [],
      };
      receivings.set(key, group);
      items.push(group);
    }
    group.count += 1;
    group.ids.push(r.receivingId);
  }

  for (const g of parcels.values()) {
    const carriers = [...g.carriers].sort().join(', ');
    g.fact = g.problem ? 'Delivery problem' : carriers || null;
  }
  for (const g of receivings.values()) {
    g.fact = g.ids.sort((a, b) => a - b).map((id) => `#${id}`).join(', ');
  }

  items.sort((a, b) =>
    a.day.localeCompare(b.day)
    || KIND_RANK[a.kind] - KIND_RANK[b.kind]
    || a.vendor.localeCompare(b.vendor));
  return items;
}

/**
 * The warehouse's calendar (/warehouse): what is coming in and what landed,
 * with none of the purchasing notes. Order-by dates are dropped — they are
 * buying deadlines, not arrivals — and every item loses what asks someone to
 * act: the late edge, a street date's release state ("12 hidden", "not set
 * up") and the delivery-problem flag. Carriers and receiving numbers stay.
 */
export const WAREHOUSE_KINDS: readonly CalendarKind[] = CALENDAR_KINDS.filter((kind) => kind !== 'due');

export function warehouseItems(items: CalendarItem[]): CalendarItem[] {
  return items
    .filter((item) => WAREHOUSE_KINDS.includes(item.kind))
    .map((item) => ({
      ...item,
      overdue: false,
      fact: item.kind === 'street' || item.fact === 'Delivery problem' ? null : item.fact,
    }));
}

const isParcels = (item: CalendarItem) => item.kind === 'arriving' || item.kind === 'delivered';

/** "7 parcels" / "2 receivings"; null for one. */
export function countText(item: CalendarItem): string | null {
  if (item.count < 2) return null;
  return `${item.count} ${isParcels(item) ? 'parcels' : item.kind === 'received' ? 'receivings' : 'items'}`;
}

/** The tooltip and accessible name of a chip: kind, journey, count, fact, in one line. */
export function describeItem(item: CalendarItem): string {
  return [KIND_LABELS[item.kind], item.vendor, item.label, countText(item), item.fact].filter(Boolean).join(' · ');
}

/**
 * A chip's second line, chosen per kind: the one thing that tells two chips of
 * the same vendor apart or says what to do. `status` leads in weight when the
 * kind has one; `text` fills what is left. Both null means a one-line chip.
 * - Order by, Ships: the release — the vendor alone does not say which; a
 *   passed order-by date leads with "Overdue".
 * - Street date: the release state ("25 hidden", "not set up"), then the release.
 * - Arriving, Delivered: the carrier, or "Delivery problem"; a passed ETA leads
 *   with "Late"; the count is a badge.
 * - Received: the receiving numbers.
 */
export function chipDetail(item: CalendarItem): { status: string | null; text: string | null } {
  switch (item.kind) {
    case 'street':
      return { status: item.fact, text: item.label };
    case 'arriving':
    case 'delivered':
      if (item.fact === 'Delivery problem') return { status: item.fact, text: null };
      return { status: item.overdue ? 'Late' : null, text: item.fact };
    case 'received':
      return { status: null, text: item.fact };
    case 'due':
      return { status: item.overdue ? 'Overdue' : null, text: item.label };
    default:
      return { status: null, text: item.label };
  }
}
